import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import { Plus, Calendar, Lock, CheckCircle, Clock } from 'lucide-react';

export default function PayrollPeriodsPage() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: ''
  });
  const [closingPeriodId, setClosingPeriodId] = useState(null);

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    try {
      const response = await api.get('/payroll/periods');
      setPeriods(response.data.items || []);
    } catch (e) {
      notify.error(e, 'PAYROLL_PERIOD_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/payroll/periods', formData);
      toast.success('Payroll period created successfully');
      setDialogOpen(false);
      setFormData({ start_date: '', end_date: '' });
      fetchPeriods();
    } catch (error) {
      notify.error(error, 'PAYROLL_PERIOD_CREATE_FAILED');
    }
  };

  const handleClose = async (periodId) => {
    setClosingPeriodId(periodId);
    try {
      await api.post(`/payroll/periods/${periodId}/close`);
      toast.success('Period closed successfully');
      fetchPeriods();
    } catch (error) {
      notify.error(error, 'PAYROLL_PERIOD_CLOSE_FAILED');
    } finally {
      setClosingPeriodId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'OPEN':
        return <Badge className="bg-green-500"><Clock className="h-3 w-3 mr-1" />Open</Badge>;
      case 'CLOSED':
        return <Badge variant="secondary"><Lock className="h-3 w-3 mr-1" />Closed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="payroll-periods-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Payroll Periods</h1>
          <p className="text-muted-foreground">Manage payroll periods for staff payments</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-period-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Period
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>Create Payroll Period</DialogTitle>
                <DialogDescription>
                  Define the date range for a new payroll period. Only one period can be open at a time.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                      data-testid="period-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">End Date</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                      data-testid="period-end-date"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-period-btn">Create Period</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            All Periods
          </CardTitle>
          <CardDescription>{periods.length} payroll period(s)</CardDescription>
        </CardHeader>
        <CardContent>
          {periods.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>No payroll periods found</p>
              <p className="text-sm mt-1">Create your first period to start processing payroll</p>
            </div>
          ) : (
            <Table data-testid="periods-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Period No</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Closed</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {periods.map((period) => (
                  <TableRow key={period.id} data-testid={`period-row-${period.id}`}>
                    <TableCell className="font-medium">{period.period_no}</TableCell>
                    <TableCell>{period.start_date}</TableCell>
                    <TableCell>{period.end_date}</TableCell>
                    <TableCell>{getStatusBadge(period.status)}</TableCell>
                    <TableCell>{new Date(period.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {period.closed_at ? new Date(period.closed_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {period.status === 'OPEN' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleClose(period.id)}
                          disabled={closingPeriodId === period.id}
                          data-testid={`close-period-${period.id}`}
                        >
                          {closingPeriodId === period.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                          ) : (
                            <>
                              <Lock className="h-4 w-4 mr-1" />
                              Close
                            </>
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
